import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { ColorBlock, SectionHeading, BodyText, BLOCKS, P, PD, A } from './BlogShared.jsx';

/* ─── Stacked Bars: Profit (green) over Waste (red), with icons ─── */
const WasteComparisonSVG = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) el.classList.add('w-active'); },
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <Box ref={ref} sx={{ width: '100%', maxWidth: 500, mx: 'auto' }}>
      <style>{`
        .w-col { opacity: 0; transform: translateY(16px); transition: all 0.7s cubic-bezier(.16,1,.3,1); }
        .w-active .w-col-l { opacity: 1; transform: translateY(0); transition-delay: 0.15s; }
        .w-active .w-col-r { opacity: 1; transform: translateY(0); transition-delay: 0.45s; }
        .w-grow { transform: scaleY(0); transform-origin: bottom; transition: transform 0.9s cubic-bezier(.16,1,.3,1); }
        .w-active .w-grow { transform: scaleY(1); }
        .w-grow-d1 { transition-delay: 0.4s; }
        .w-grow-d2 { transition-delay: 0.6s; }
        .w-grow-d3 { transition-delay: 0.5s; }
        .w-grow-d4 { transition-delay: 0.7s; }
        .w-icon { opacity: 0; transition: opacity 0.5s ease; }
        .w-active .w-icon { opacity: 1; transition-delay: 1s; }
      `}</style>
      <svg viewBox="0 0 500 320" fill="none" style={{ width: '100%', display: 'block' }}>
        <defs>
          {/* Profit (green) gradient */}
          <linearGradient id="wProfit" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#66BB6A" />
            <stop offset="100%" stopColor="#2E7D32" />
          </linearGradient>
          {/* Waste (red) gradient */}
          <linearGradient id="wWaste" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={A} />
            <stop offset="100%" stopColor="#C62828" />
          </linearGradient>
          {/* Glass panel */}
          <linearGradient id="wGlass" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="white" stopOpacity="0.65" />
            <stop offset="100%" stopColor="white" stopOpacity="0.25" />
          </linearGradient>
          <filter id="wShadow">
            <feDropShadow dx="0" dy="3" stdDeviation="6" floodColor={PD} floodOpacity="0.10" />
          </filter>
        </defs>

        {/* ═══ LEFT: Without Karobar ═══ */}
        <g className="w-col w-col-l">
          {/* Background panel */}
          <rect x="15" y="10" width="210" height="300" rx="18" fill="url(#wGlass)" stroke={`${A}12`} strokeWidth="1" filter="url(#wShadow)" />
          <rect x="15" y="10" width="210" height="3" rx="1.5" fill="rgba(255,255,255,0.5)" />

          {/* Label */}
          <text x="120" y="38" textAnchor="middle" fill={A} fontSize="11" fontWeight="600" fontFamily="Inter, sans-serif" opacity="0.85">Without Karobar</text>

          {/* Stacked bar container — total height 220, starts at y=55, 5px gap */}
          {/* Earnings bar (top) — 50% = 107px */}
          <g className="w-grow w-grow-d1">
            <rect x="55" y="55" width="130" height="107" rx="14" fill="url(#wProfit)" />
            <rect x="55" y="55" width="130" height="12" rx="6" fill="rgba(255,255,255,0.18)" />
            <rect x="62" y="62" width="5" height="70" rx="2.5" fill="rgba(255,255,255,0.12)" />
          </g>

          {/* Losses bar (bottom) — 50% = 107px, 5px gap */}
          <g className="w-grow w-grow-d2">
            <rect x="55" y="167" width="130" height="107" rx="14" fill="url(#wWaste)" />
            <rect x="55" y="167" width="130" height="12" rx="6" fill="rgba(255,255,255,0.15)" />
            <rect x="62" y="174" width="5" height="70" rx="2.5" fill="rgba(255,255,255,0.10)" />
          </g>

          {/* ₹ icon on earnings section */}
          <g className="w-icon">
            <circle cx="120" cy="103" r="16" fill="rgba(255,255,255,0.2)" />
            <text x="120" y="110" textAnchor="middle" fill="white" fontSize="16" fontWeight="700" fontFamily="Inter, sans-serif">₹</text>
            <text x="120" y="128" textAnchor="middle" fill="rgba(255,255,255,0.85)" fontSize="9" fontWeight="600" fontFamily="Inter, sans-serif">Earnings</text>
          </g>

          {/* Dustbin icon on losses section */}
          <g className="w-icon">
            <g transform="translate(105, 200)">
              {/* Bin body */}
              <path d="M3 8 L5 28 Q5 30 7 30 L23 30 Q25 30 25 28 L27 8 Z" fill="rgba(255,255,255,0.25)" stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
              {/* Bin lid */}
              <rect x="1" y="5" width="28" height="4" rx="2" fill="rgba(255,255,255,0.35)" />
              {/* Lid handle */}
              <rect x="11" y="2" width="8" height="4" rx="2" fill="rgba(255,255,255,0.3)" />
              {/* Bin lines */}
              <line x1="10" y1="12" x2="10" y2="26" stroke="rgba(255,255,255,0.3)" strokeWidth="1" strokeLinecap="round" />
              <line x1="15" y1="12" x2="15" y2="26" stroke="rgba(255,255,255,0.3)" strokeWidth="1" strokeLinecap="round" />
              <line x1="20" y1="12" x2="20" y2="26" stroke="rgba(255,255,255,0.3)" strokeWidth="1" strokeLinecap="round" />
            </g>
            <text x="120" y="250" textAnchor="middle" fill="rgba(255,255,255,0.85)" fontSize="9" fontWeight="600" fontFamily="Inter, sans-serif">Losses</text>
          </g>
        </g>

        {/* ═══ RIGHT: With Karobar ═══ */}
        <g className="w-col w-col-r">
          {/* Background panel */}
          <rect x="275" y="10" width="210" height="300" rx="18" fill="url(#wGlass)" stroke={`${P}12`} strokeWidth="1" filter="url(#wShadow)" />
          <rect x="275" y="10" width="210" height="3" rx="1.5" fill="rgba(255,255,255,0.7)" />

          {/* Label */}
          <text x="380" y="38" textAnchor="middle" fill={P} fontSize="11" fontWeight="600" fontFamily="Inter, sans-serif" opacity="0.9">With Karobar</text>

          {/* Stacked bar container — total height 220, starts at y=55, 5px gap */}
          {/* Earnings bar (top) — 90% = 192px */}
          <g className="w-grow w-grow-d3">
            <rect x="315" y="55" width="130" height="192" rx="14" fill="url(#wProfit)" />
            <rect x="315" y="55" width="130" height="12" rx="6" fill="rgba(255,255,255,0.18)" />
            <rect x="322" y="62" width="5" height="130" rx="2.5" fill="rgba(255,255,255,0.12)" />
          </g>

          {/* Losses bar (bottom) — 10% = 22px, 5px gap */}
          <g className="w-grow w-grow-d4">
            <rect x="315" y="252" width="130" height="22" rx="11" fill="url(#wWaste)" opacity="0.7" />
          </g>

          {/* ₹ icon on earnings section */}
          <g className="w-icon">
            <circle cx="380" cy="140" r="18" fill="rgba(255,255,255,0.2)" />
            <text x="380" y="148" textAnchor="middle" fill="white" fontSize="18" fontWeight="700" fontFamily="Inter, sans-serif">₹</text>
            <text x="380" y="170" textAnchor="middle" fill="rgba(255,255,255,0.85)" fontSize="9" fontWeight="600" fontFamily="Inter, sans-serif">Earnings</text>
          </g>

          {/* Dustbin icon on losses sliver */}
          <g className="w-icon">
            <g transform="translate(370, 253)">
              {/* Tiny bin */}
              <path d="M2 4 L3 14 Q3 15 4 15 L16 15 Q17 15 17 14 L18 4 Z" fill="rgba(255,255,255,0.3)" stroke="rgba(255,255,255,0.5)" strokeWidth="0.8" />
              <rect x="1" y="2" width="18" height="3" rx="1.5" fill="rgba(255,255,255,0.35)" />
              <rect x="7" y="0" width="6" height="3" rx="1.5" fill="rgba(255,255,255,0.3)" />
            </g>
          </g>
        </g>

        {/* ═══ Center divider ═══ */}
        <line x1="250" y1="35" x2="250" y2="290" stroke={`${PD}08`} strokeWidth="1" strokeDasharray="4 6" />
      </svg>
    </Box>
  );
};

const features = [
  { text: 'Predicts demand from historical data — prep only what actually sells' },
  { text: 'Adjusts in real-time: slow lunch means less evening prep, automatically' },
  { text: 'Learns waste patterns over time and sharpens accuracy week by week' },
  { text: 'Tracks shelf life — alerts before anything expires' },
];

export const WasteSection = () => (
  <ColorBlock bg={BLOCKS.lightPink} innerBg="white" innerRadius="32px">
    {/* Header */}
    <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
      <SectionHeading size="lg" align="center" sx={{ color: PD, mb: 2 }}>
        <Box component="span" sx={{ fontFamily: "'Instrument Serif', serif", fontWeight: 700, fontStyle: 'italic' }}>Every</Box> item prepared
        <br />
        should be an item <Box component="span" sx={{ fontFamily: "'Instrument Serif', serif", fontWeight: 700, fontStyle: 'italic' }}>sold.</Box>
      </SectionHeading>
    </Box>

    {/* SVG + text side-by-side */}
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 4, md: 6 }, alignItems: 'center' }}>
      <Box sx={{ flex: '1 1 55%', minWidth: 0 }}>
        <WasteComparisonSVG />
      </Box>
      <Box sx={{ flex: '1 1 45%', minWidth: 0 }}>
        <Typography sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, fontWeight: 500, color: PD, mb: 1.5, lineHeight: 1.5 }}>
          Most kitchens prep on gut feel.
          <br />
          Karobar <Box component="span" sx={{ fontFamily: "'Instrument Serif', serif", fontWeight: 700, fontStyle: 'italic' }}>predicts</Box> demand — so nothing goes to waste.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 3 }}>
          {features.map((f, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: i % 2 === 0 ? P : A, opacity: 0.6, flexShrink: 0, mt: '8px' }} />
              <Typography sx={{ fontSize: '0.88rem', color: 'rgba(0,0,0,0.65)', lineHeight: 1.7 }}>
                {f.text}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  </ColorBlock>
);
